import { useEffect, useReducer, useState } from 'react';
import { GOODS_TYPE, PlanDataType, customTeamPlan, freePlanData, proPlanData, teamPlanData } from '../utils';
import NP from 'number-precision';
import { WEB_APP_EN_SIGN_UP_URL, WEB_APP_HOME_URL, WEB_APP_JP_SIGN_UP_URL } from '@/constants';
import { numericPricingPageSeats } from '@/utils';
export interface IUsePlansData {
  lang: 'ja' | 'en';
}

export type TFormInfo = {
  seats: number;
  duration: number;
  payType: 'year' | 'month';
  language: 'en' | 'ja';
};

export type TDispatchFormInfoParams =
  | {
      type: 'seats';
      value: number;
    }
  | {
      type: 'duration';
      value: number;
    }
  | {
      type: 'payType';
      value: 'year' | 'month';
    };

export interface TFormReducer {
  (state: TFormInfo, { type, value }: TDispatchFormInfoParams): TFormInfo;
}

export const formReducer: TFormReducer = (state, { type, value }) => {
  switch (type) {
    case 'seats':
      return { ...state, seats: value as number };
    case 'duration':
      return { ...state, duration: value as number };
    case 'payType':
      return { ...state, payType: value as 'year' | 'month' };
    default:
      return state;
  }
};

export interface IUnitPriceItemInfo {
  price: number;
  stripId: string;
  seatsPrice: number;
}

export interface IUnitPriceInfo {
  [key: number]: {
    month: IUnitPriceItemInfo;
    year: IUnitPriceItemInfo;
  };
}

/**
 * 基础席位
 */
export const BASIC_SEATS = 2;
/**
 * 分转为元
 */
export const CENTS_TO_DOLLAR = 100;
/**
 * 一年12个月
 */
export const MONTH_COUNT_OF_YEAR = 12;
/**
 * 英文价格保留2位小数
 */
export const FORMATTED_EN_PRICE = 2;
/**
 * 日文价格不保留小数
 */
export const FORMATTED_JP_PRICE = 0;

export interface PlanUnitPriceInfoV2 {
  month: {
    realPrice: number;
  };
  year: {
    realPrice: number;
  };
}

export enum CurrencyType {
  CNY = 'CNY',
  TWD = 'TWD',
  JPY = 'JPY',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  KRW = 'KRW',
  MYR = 'MYR',
  IDR = 'IDR',
}

const teamPlanPriceInfo = {
  ja: {
    month: {
      realPrice: 418000,
    },
    year: {
      realPrice: 3009600,
    },
  },
  en: {
    month: {
      realPrice: 2799,
    },
    year: {
      realPrice: 19999,
    },
  },
};

const usePlansData = ({ lang }: IUsePlansData) => {
  const initFormInfo: TFormInfo = {
    seats: 1,
    duration: 2400,
    payType: 'year',
    language: lang,
  };
  /**
   * 各种plan的信息
   */
  const [planInfo, setPlanInfo] = useState<PlanDataType[]>([freePlanData, proPlanData, teamPlanData, customTeamPlan]);
  /**
   * 变量：席位，时长，年付/月付
   */
  const [formInfo, dispatchFormInfo] = useReducer<TFormReducer>(formReducer, initFormInfo);
  /**
   * 时长对应的价格信息，包含：价格，席位价格，stripId
   */
  const teamPlanUnitPriceInfo: PlanUnitPriceInfoV2 = teamPlanPriceInfo[lang];

  const getUpgradeLink = (type: GOODS_TYPE) => {
    if (type === GOODS_TYPE.FREE) {
      return lang === 'ja' ? WEB_APP_JP_SIGN_UP_URL : WEB_APP_EN_SIGN_UP_URL;
    }

    if (type === GOODS_TYPE.TEAM_ENTERPRISE) {
      return lang === 'ja' ? '/enterprise' : '/en/contact';
    }

    if (type === GOODS_TYPE.PRO) {
      return `${WEB_APP_HOME_URL}&language=${lang}&pay_cycle=${formInfo.payType}&seats=1&subscribe_type=pro`;
    }

    if (type === GOODS_TYPE.TEAM) {
      return `${WEB_APP_HOME_URL}&language=${lang}&pay_cycle=${formInfo.payType}&seats=${numericPricingPageSeats(
        formInfo.seats,
      )}&subscribe_type=team`;
    }

    return WEB_APP_HOME_URL;
  };

  useEffect(() => {
    const seats = numericPricingPageSeats(formInfo.seats);
    const needModifyData = {
      month: NP.round(
        NP.divide(teamPlanUnitPriceInfo.month.realPrice * seats, CENTS_TO_DOLLAR),
        lang === 'en' ? FORMATTED_EN_PRICE : FORMATTED_JP_PRICE,
      ),
      year: NP.round(
        NP.divide(teamPlanUnitPriceInfo.year.realPrice * seats, MONTH_COUNT_OF_YEAR * CENTS_TO_DOLLAR),
        lang === 'en' ? FORMATTED_EN_PRICE : FORMATTED_JP_PRICE,
      ),
    };

    setPlanInfo((pre) => {
      const newPlanInfo = pre.map((item) => {
        if (item.type === GOODS_TYPE.TEAM) {
          return {
            ...item,
            averagePrice: {
              ...item.averagePrice,
              [lang]: needModifyData,
            },
          };
        }
        return item;
      });
      return newPlanInfo;
    });
  }, [formInfo.seats, lang, teamPlanUnitPriceInfo.month.realPrice, teamPlanUnitPriceInfo.year.realPrice]);

  return {
    planInfo,
    formInfo,
    teamPlanUnitPriceInfo,
    dispatchFormInfo,
    getUpgradeLink,
  };
};

export default usePlansData;
